const toSafeNumber = (value: any): number => {
  if (value === null || value === undefined || value === "") {
    return 0;
  }

  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : 0;
};

export const normalizeInvoiceListTaxRate = (value: any): string => {
  const rawValue =
    value && typeof value === "object"
      ? value.code ?? value.name ?? value.taxValue
      : value;
  const normalizedValue = `${rawValue ?? ""}`.trim();

  const specialTaxRates = {
    "-1": "KCT",
    "-2": "KKKNT",
    "-3": "KHAC",
    KCT: "KCT",
    KKKNT: "KKKNT",
    KHAC: "KHAC",
  };
  const specialTaxRate =
    specialTaxRates[normalizedValue.toUpperCase()];
  if (specialTaxRate) {
    return specialTaxRate;
  }

  const numericTaxRate = normalizedValue.replace(/%$/, "");
  if (numericTaxRate !== "" && Number.isFinite(Number(numericTaxRate))) {
    return `${Number(numericTaxRate)}%`;
  }

  return normalizedValue;
};

const getTaxRate = (value: any): number => {
  const normalizedValue = `${value ?? ""}`.trim().replace(/%$/, "");
  return toSafeNumber(normalizedValue);
};

export const calculateInvoiceListItemDiff = (item: any) => {
  const normalizedItem = {
    ...item,
    originalTaxRate: normalizeInvoiceListTaxRate(item?.originalTaxRate),
    updatedTaxRate: normalizeInvoiceListTaxRate(item?.updatedTaxRate),
  };

  return {
    ...normalizedItem,
    beforeTaxAmountDiff:
      toSafeNumber(normalizedItem.updatedAmountBeforeTax) -
      toSafeNumber(normalizedItem.originalAmountBeforeTax),
    taxAmountDiff:
      toSafeNumber(normalizedItem.updatedTax) -
      toSafeNumber(normalizedItem.originalTax),
    discountAmountDiff:
      toSafeNumber(normalizedItem.updatedDiscountAmount) -
      toSafeNumber(normalizedItem.originalDiscountAmount),
    amountAfterTaxDiff:
      toSafeNumber(normalizedItem.updatedAmountAfterTax) -
      toSafeNumber(normalizedItem.originalAmountAfterTax),
  };
};

/**
 * Tính lại các giá trị phụ thuộc của một dòng bảng kê sau khi người dùng sửa dữ liệu.
 *
 * @param item Dòng bảng kê cần tính lại.
 * @param changedField Tên trường vừa được thay đổi.
 * @returns Dòng bảng kê sau khi tính lại và cập nhật các cột chênh lệch.
 */
export const recalculateInvoiceListItem = (item: any, changedField: string) => {
  const calculatedItem = { ...item };
  const fieldPrefix = changedField.startsWith("original")
    ? "original"
    : "updated";
  const quantityField = `${fieldPrefix}Quantity`;
  const unitPriceField = `${fieldPrefix}UnitPrice`;
  const amountBeforeTaxField = `${fieldPrefix}AmountBeforeTax`;
  const taxRateField = `${fieldPrefix}TaxRate`;
  const taxField = `${fieldPrefix}Tax`;
  const discountAmountField = `${fieldPrefix}DiscountAmount`;
  const amountAfterTaxField = `${fieldPrefix}AmountAfterTax`;

  // Hai nhóm số liệu phải dùng cùng công thức để cột chênh lệch phản ánh đúng dữ liệu trước và sau điều chỉnh.
  const shouldCalculateAmountBeforeTax = [
    quantityField,
    unitPriceField,
  ].includes(changedField);
  const shouldCalculateTax =
    shouldCalculateAmountBeforeTax ||
    [amountBeforeTaxField, taxRateField].includes(changedField);
  const shouldCalculateAmountAfterTax =
    shouldCalculateTax ||
    [taxField, discountAmountField].includes(changedField);

  if (shouldCalculateAmountBeforeTax) {
    calculatedItem[amountBeforeTaxField] =
      toSafeNumber(calculatedItem[quantityField]) *
      toSafeNumber(calculatedItem[unitPriceField]);
  }

  if (shouldCalculateTax) {
    calculatedItem[taxField] =
      (toSafeNumber(calculatedItem[amountBeforeTaxField]) *
        getTaxRate(calculatedItem[taxRateField])) /
      100;
  }

  if (shouldCalculateAmountAfterTax) {
    calculatedItem[amountAfterTaxField] =
      toSafeNumber(calculatedItem[amountBeforeTaxField]) +
      toSafeNumber(calculatedItem[taxField]) -
      toSafeNumber(calculatedItem[discountAmountField]);
  }

  return calculateInvoiceListItemDiff(calculatedItem);
};
